import React, { useEffect, useRef } from "react";
import { Button } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTour } from "./TourProvider";
import { getTourForRoute } from "./utils/getTourForRoute";
import { resolveTourSteps } from "./utils/resolveTourSteps";
import {
  getPathnameWithoutQuery,
  isPropertyDetailsPath,
  isSetupWalkthroughRoute,
} from "./utils/setupWalkthrough";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";

const PENDING_TOUR_MAX_ATTEMPTS = 24;
const PENDING_TOUR_RETRY_MS = 150;

const clickTab = (tabKey: string): void => {
  const tabElement = document.querySelector(
    `[data-node-key="${tabKey}"]`
  ) as HTMLElement | null;
  if (tabElement) {
    tabElement.click();
  }
};

export const GuideButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    open,
    startTour,
    closeTour,
    pendingTour,
    clearPendingTour,
    setupWalkthroughActive,
    setupWalkthroughPhase,
  } = useTour();
  const { t } = useTranslation();
  const hasAutoStarted = useRef(false);
  const lastSetupAutoPathRef = useRef<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");

  // Allow resume again whenever the route or setup phase changes
  useEffect(() => {
    lastSetupAutoPathRef.current = null;
    hasAutoStarted.current = false;
  }, [location.pathname, setupWalkthroughPhase]);

  // Auto-start tour if there's a pending tour for this page/tab
  useEffect(() => {
    if (!pendingTour) {
      hasAutoStarted.current = false;
      return;
    }

    const pendingPathname = getPathnameWithoutQuery(pendingTour.path);
    const isMatchingPath = location.pathname === pendingPathname;
    if (!isMatchingPath) {
      return;
    }

    if (pendingTour.tab) {
      const urlParams = new URLSearchParams(location.search);
      const currentTab = urlParams.get("tab");
      if (currentTab !== pendingTour.tab) {
        // Sync URL + Ant Design Tabs, then retry when search updates
        clickTab(pendingTour.tab);
        navigate(`${location.pathname}?tab=${pendingTour.tab}`, {
          replace: true,
        });
        return;
      }
      // Ensure the tab panel is actually active (controlled/defaultActiveKey races)
      clickTab(pendingTour.tab);
    }

    if (hasAutoStarted.current) {
      return;
    }

    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tryStartPendingTour = () => {
      const tourConfig = getTourForRoute(
        location.pathname,
        t,
        setupWalkthroughActive
          ? { setup: true, setupPhase: setupWalkthroughPhase }
          : undefined
      );
      if (!tourConfig) {
        clearPendingTour();
        return;
      }

      const resolved = resolveTourSteps(tourConfig);
      if (resolved.length > 0) {
        hasAutoStarted.current = true;
        lastSetupAutoPathRef.current = location.pathname;
        clearPendingTour();
        requestAnimationFrame(() => startTour(resolved));
        return;
      }

      attempt += 1;
      if (attempt < PENDING_TOUR_MAX_ATTEMPTS) {
        timer = setTimeout(tryStartPendingTour, PENDING_TOUR_RETRY_MS);
        return;
      }

      clearPendingTour();
    };

    timer = setTimeout(tryStartPendingTour, PENDING_TOUR_RETRY_MS);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    pendingTour,
    location.pathname,
    location.search,
    t,
    startTour,
    clearPendingTour,
    setupWalkthroughActive,
    setupWalkthroughPhase,
    navigate,
  ]);

  // Setup walkthrough fallback: auto-resume when no pendingTour was queued
  useEffect(() => {
    if (!setupWalkthroughActive || open || pendingTour) {
      return;
    }

    if (!isSetupWalkthroughRoute(location.pathname)) {
      return;
    }

    if (lastSetupAutoPathRef.current === location.pathname) {
      return;
    }

    if (
      isPropertyDetailsPath(location.pathname) &&
      (setupWalkthroughPhase === "building" ||
        setupWalkthroughPhase === "property")
    ) {
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.get("tab") !== "buildingList") {
        clickTab("buildingList");
        navigate(`${location.pathname}?tab=buildingList`, { replace: true });
        return;
      }
      clickTab("buildingList");
    }

    if (
      location.pathname.startsWith("/building/") &&
      setupWalkthroughPhase === "unit"
    ) {
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.get("tab") !== "unitList") {
        clickTab("unitList");
        navigate(`${location.pathname}?tab=unitList`, { replace: true });
        return;
      }
      clickTab("unitList");
    }

    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const tryStartSetupTour = () => {
      if (cancelled) {
        return;
      }

      const tourConfig = getTourForRoute(location.pathname, t, {
        setup: true,
        setupPhase: setupWalkthroughPhase,
      });
      if (!tourConfig) {
        lastSetupAutoPathRef.current = location.pathname;
        return;
      }

      const resolved = resolveTourSteps(tourConfig);
      if (resolved.length > 0) {
        lastSetupAutoPathRef.current = location.pathname;
        requestAnimationFrame(() => {
          if (!cancelled) {
            startTour(resolved);
          }
        });
        return;
      }

      attempt += 1;
      if (attempt < PENDING_TOUR_MAX_ATTEMPTS) {
        timer = setTimeout(tryStartSetupTour, PENDING_TOUR_RETRY_MS);
        return;
      }

      // Do not permanently lock the path — leave ref unset so a later paint can retry
    };

    timer = setTimeout(tryStartSetupTour, PENDING_TOUR_RETRY_MS);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    setupWalkthroughActive,
    setupWalkthroughPhase,
    open,
    pendingTour,
    location.pathname,
    location.search,
    navigate,
    t,
    startTour,
  ]);

  useEffect(() => {
    if (!setupWalkthroughActive) {
      lastSetupAutoPathRef.current = null;
    }
  }, [setupWalkthroughActive]);

  const handleClick = () => {
    const options = setupWalkthroughActive
      ? { setup: true as const, setupPhase: setupWalkthroughPhase }
      : undefined;
    if (open) {
      closeTour();
      setTimeout(() => {
        const tourConfig = getTourForRoute(location.pathname, t, options);
        if (tourConfig) {
          startTour(tourConfig);
        }
      }, 100);
    } else {
      const tourConfig = getTourForRoute(location.pathname, t, options);
      if (tourConfig) {
        startTour(tourConfig);
      }
    }
  };

  const tourConfig = getTourForRoute(
    location.pathname,
    t,
    setupWalkthroughActive
      ? { setup: true, setupPhase: setupWalkthroughPhase }
      : undefined
  );
  const isTourAvailable = !!tourConfig;

  if (!isTourAvailable) {
    return null;
  }

  return (
    <Button
      type="primary"
      icon={<QuestionCircleOutlined />}
      style={{ marginRight: "0.5rem" }}
      onClick={handleClick}
      title={t("Start_Guide_Tour") || "Start Guide Tour"}
    >
      {!isMobile && (t("Guide") || "Guide")}
    </Button>
  );
};
